import axios from "axios";
import format from "date-fns/format";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import useProducts from "fe-shared/src/hooks/useProducts";
import { getOffsetDate } from "fe-shared/src/helpers/dates";

import { BusinessRole, MaterialUnit } from "fe-shared/src/types/domain";
import { ExcelColumnConfig } from "fe-shared/src/types/common";
import {
  DataGridRequestParams,
  GridTransactionExport,
  GridTransactionList,
  NoteRecord,
  TransactionMaterial,
  TransactionType,
} from "../types";
import { formatRecord } from "../helpers/format";
import { utcToZonedTime } from "date-fns-tz";

const limit = 5000;
const MAX_STRING_LENGTH = 32767;

export interface HistoryLogsResponse {
  [key: string]: NoteRecord[];
}

const getFormattedDate = (dateString?: string) => {
  if (!dateString) {
    return "";
  }
  const time = dateString?.substr(11, 8);
  const isNoOffset = time === "00:00:00";

  const date = isNoOffset
    ? getOffsetDate(new Date(dateString))!
    : new Date(dateString);

  return format(date, "MMM d, yyyy");
};

const getRelatedAdjustment = (i: GridTransactionExport) =>
  i.linkedTransactions.find(
    (lt) => lt.transactionType === TransactionType.Adjustment
  );

export const getTransactionsExport = async (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  let skip = 0;
  let total = 0;
  const transactions: GridTransactionExport[] = [];

  do {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await axios.post<
      GridTransactionList<GridTransactionExport>
    >(
      "getTransactions/export",
      {
        ...requestParams,
        skip,
        limit,
      },
      { params: { companyId } }
    );

    total = data.total;
    skip += limit;
    transactions.push(...data.transactions);
  } while (skip < total);

  if (!companyId) {
    const transactionNumbers = transactions.map((t) => t.transactionNumber);

    const historyLogsByInvoiceNumber = await axios
      .post<HistoryLogsResponse>("history/fetchForExport", {
        objectIds: transactionNumbers,
        entityType: "transaction",
      })
      .then((res) => res.data);

    return transactions.map((i) => ({
      ...i,
      historyLogs: historyLogsByInvoiceNumber[i.transactionNumber],
    }));
  }

  return transactions;
};

const useTransactionsExport = (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  const { t } = useTranslation();
  const { data } = useRecycleMaterials();
  const { data: products } = useProducts();
  const { data: recoveryMaterials } = useProducts("Retreading");
  const isStaff = !companyId;

  const tires = useMemo(
    () =>
      (data || []).filter((tire) =>
        tire.allowedRoles?.includes(BusinessRole.Processor)
      ),
    [data]
  );

  const getDataRows = () => getTransactionsExport(requestParams, companyId);

  const columnConfig = useMemo(() => {
    const columns: ExcelColumnConfig<GridTransactionExport>[] = [
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.transactionNumber"),
        getOriginalValue: (i) => i.transactionNumber,
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.sourceParticipantId"),
        getOriginalValue: (i) => i.sourceParticipantRegNumber,
      },
      {
        width: 40,
        mappedKey: t("transactions.grid.export.cols.sourceParticipant"),
        getOriginalValue: (i) => i.sourceParticipantDisplayName,
      },
      {
        width: 20,
        mappedKey: t(
          "transactions.grid.export.cols.sourceParticipantBusinessRoleCode"
        ),
        getOriginalValue: (i) =>
          t(`businessRoles.${i.sourceParticipantBusinessRoleCode}`),
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.targetParticipantId"),
        getOriginalValue: (i) => i.targetParticipantRegNumber,
      },
      {
        width: 40,
        mappedKey: t("transactions.grid.export.cols.targetParticipant"),
        getOriginalValue: (i) => i.targetParticipantDisplayName,
      },
      {
        width: 20,
        mappedKey: t(
          "transactions.grid.export.cols.targetParticipantBusinessRoleCode"
        ),
        getOriginalValue: (i) =>
          t(`businessRoles.${i.targetParticipantBusinessRoleCode}`),
      },
      ...(!isStaff
        ? [
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.sortYardId"),
              getOriginalValue: (i: GridTransactionExport) =>
                i.sortYardId > 0
                  ? t("transfers.grid.export.cols.sortYardIdValue", {
                      id: i.sortYardId,
                    })
                  : "",
            },
          ]
        : []),
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.scaleTicket"),
        getOriginalValue: (i) =>
          i.scaleTicketNumbers?.filter(Boolean).join(", "),
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.scaleTicketDate"),
        getOriginalValue: (i) =>
          i.scaleTicketDates?.filter(Boolean).map(getFormattedDate).join(", "),
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.dispositionInvoiceNumber"),
        getOriginalValue: (i) => i.dispositionInvoiceNumber,
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.dispositionInvoiceDate"),
        getOriginalValue: (i) => getFormattedDate(i.dispositionInvoiceDate),
      },
      {
        width: 15,
        mappedKey: t("transactions.grid.export.cols.invoiceNumber"),
        getOriginalValue: (i) => i.invoiceNumber,
      },
      {
        width: 15,
        mappedKey: t("transactions.grid.export.cols.invoiceDate"),
        getOriginalValue: (i) => getFormattedDate(i.invoiceDate),
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.paymentAmount"),
        getOriginalValue: (i: GridTransactionExport) => i.paymentAmount
      },
      ...(isStaff
        ? [
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.retreadingType"),
              getOriginalValue: (i: GridTransactionExport) => i.retreadingType,
            },
            {
              width: 20,
              mappedKey: t(
                "transactions.grid.export.cols.productionReportDate"
              ),
              getOriginalValue: (i: GridTransactionExport) =>
                getFormattedDate(i.productionReportDate),
            },
            {
              width: 20,
              mappedKey: t(
                "transactions.grid.export.cols.retreadingPeriodDate"
              ),
              getOriginalValue: (i: GridTransactionExport) => {
                if (!i.retreadingPeriodDate) return "";

                return format(utcToZonedTime(new Date((i.retreadingPeriodDate)), "UTC"), "MMM, yyyy");
              },
            },
          ]
        : []),
      {
        width: 15,
        mappedKey: t("transactions.grid.export.cols.initiatedDate"),
        getOriginalValue: (i) => getFormattedDate(i.initiatedDate),
      },
      {
        width: 15,
        mappedKey: t("transactions.grid.export.cols.completedDate"),
        getOriginalValue: (i) => getFormattedDate(i.completedDate),
      },
      {
        width: 15,
        mappedKey: t("transactions.grid.export.cols.syncedDate"),
        getOriginalValue: (i) => getFormattedDate(i.syncedDate),
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.type"),
        getOriginalValue: (i) => t(`transactions.type.${i.transactionType}`),
      },
      ...(isStaff
        ? [
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.routeSource"),
              getOriginalValue: (i: GridTransactionExport) => i.sourceRoute,
            },
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.assignee"),
              getOriginalValue: (i: GridTransactionExport) =>
                i.assigneeDisplayName,
            },
          ]
        : []),
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.statusCode"),
        getOriginalValue: (i) =>
          `${t(`transactions.statuses.${i.statusCode}`)}${
            i.isAdjustable || i.isValidating ? " -" : ""
          }`,
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.adjustmentNumber"),
        getOriginalValue: (i) => getRelatedAdjustment(i)?.transactionNumber,
      },
      {
        width: 20,
        mappedKey: t("transactions.grid.export.cols.adjustmentStatusCode"),
        getOriginalValue: (i) => {
          const adjustment = getRelatedAdjustment(i);

          return adjustment
            ? t(`transactions.statuses.${adjustment.transactionStatus}`)
            : "";
        },
      },
      {
        width: 30,
        mappedKey: t("transactions.grid.export.cols.sourceParticipantUser"),
        getOriginalValue: (i) => i.sourceSignerDisplayName,
      },
      {
        width: 30,
        mappedKey: t("transactions.grid.export.cols.targetParticipantUser"),
        getOriginalValue: (i) => i.targetSignerDisplayName,
      },
      ...tires.map((tire) => ({
        width: 15,
        mappedKey: t("transactions.grid.export.cols.tireCount", {
          code: tire.code,
        }),
        getOriginalValue: (i: GridTransactionExport) => {
          const material: TransactionMaterial | undefined = i.materials?.find(
            (m) =>
              m.materialCode === tire.code &&
              m.units.unit === MaterialUnit.Units
          );

          return material !== undefined
            ? Math.ceil(material.units.unitQuantity)
            : "";
        },
      })),
      {
        width: 30,
        mappedKey: t("transactions.grid.export.cols.estimatedWeight"),
        getOriginalValue: (i) => i.estimatedWeight,
      },
      {
        width: 30,
        mappedKey: t("transactions.grid.export.cols.netWeight"),
        getOriginalValue: (i) => i.netWeight,
      },
      {
        width: 30,
        mappedKey: t("transactions.grid.export.cols.weightVariancePercentage"),
        getOriginalValue: (i) =>
          i.weightVariancePercentage
            ? `${(Math.round(i.weightVariancePercentage * 10000) / 100).toFixed(
                2
              )}%`
            : "-",
      },
      ...tires.map((tire) => ({
        width: 15,
        mappedKey: t("transactions.grid.export.cols.tireWeight", {
          code: tire.code,
        }),
        getOriginalValue: (i: GridTransactionExport) =>
          i.materials?.find(
            (m) =>
              m.materialCode === tire.code && m.units.unit === MaterialUnit.Kg
          )?.units.unitQuantity,
      })),
      ...(isStaff
        ? [
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.recoveryGain"),
              getOriginalValue: (i: GridTransactionExport) => i.recoveryGain,
            },
            ...(recoveryMaterials || []).map((recoveryMaterial) => ({
              width: 15,
              mappedKey: t("transactions.grid.export.cols.tireWeight", {
                code: recoveryMaterial.name,
              }),
              getOriginalValue: (i: GridTransactionExport) =>
                i.materials?.find(
                  (m) =>
                    m.materialCode === recoveryMaterial.code &&
                    m.units.unit === MaterialUnit.Kg
                )?.units.unitQuantity,
            })),
          ]
        : []),
      ...(products || []).map((product) => ({
        width: 15,
        mappedKey: t("transactions.grid.export.cols.tireWeight", {
          code: product.name,
        }),
        getOriginalValue: (i: GridTransactionExport) =>
          i.materials?.find(
            (m) =>
              m.materialCode === product.code &&
              m.units.unit === MaterialUnit.Kg
          )?.units.unitQuantity,
      })),
      ...(isStaff
        ? [
            {
              width: 30,
              mappedKey: t("transactions.grid.export.cols.isEdited"),
              getOriginalValue: (i: GridTransactionExport) =>
                t(i.isEdited ? "yes" : "no"),
            },
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.notes"),
              getOriginalValue: (i: GridTransactionExport) => {
                const notes = (i.notes || []).map(formatRecord).join("\n");

                return notes.length < MAX_STRING_LENGTH
                  ? notes
                  : `${notes.substring(0, MAX_STRING_LENGTH - 3)}...`;
              },
            },
            {
              width: 20,
              mappedKey: t("transactions.grid.export.cols.historyLogs"),
              getOriginalValue: (i: GridTransactionExport) => {
                const historyLogs = (i.historyLogs || [])
                  .map(formatRecord)
                  .join("\n");

                return historyLogs.length < MAX_STRING_LENGTH
                  ? historyLogs
                  : `${historyLogs.substring(0, MAX_STRING_LENGTH - 3)}...`;
              },
            },
          ]
        : []),
    ];

    return columns;
  }, [t, isStaff, tires, recoveryMaterials, products]);

  return {
    getDataRows,
    columnConfig,
  };
};

export default useTransactionsExport;
